// src/app/services/chat.service.ts
import { Injectable, OnDestroy, WritableSignal, computed, inject, signal } from '@angular/core';
import { AuthService } from './auth.service';
import { FirestoreService } from './firestore.service';
import { BloqueosService } from './bloqueos.service';
import { EvaluarService } from './evaluar.service';
import { ChatMediaService } from './chat-media.service';
import { map, Observable } from 'rxjs';
import { Timestamp, where, orderBy } from 'firebase/firestore';
import { EstadisticasService } from './estadisticas.service';

export interface ChatUsuario {
  uid: string;
  name: string;
  profilePhoto: string;
  photos: string[];
}

export interface ChatSummary {
  uid: string;
  nombre: string;
  foto: string;
  promedioGeneral?: number;
  mensajesNoLeidos: number;
  ultimoMensaje: {
    texto: string;
    uid: string;      // quien envió
    fecha: Timestamp;
    leido: boolean;
  };
}

@Injectable({ providedIn: 'root' })
export class ChatService implements OnDestroy {
  private authService = inject(AuthService);
  private firestoreService = inject(FirestoreService);
  private bloqueosService = inject(BloqueosService);
  private evaluarService = inject(EvaluarService);
  private chatMediaService = inject(ChatMediaService);
  private estadisticasService = inject(EstadisticasService);

  private unsubAuth?: () => void;
  private unsubChats?: () => void;
  evaluacionActivaMap = new Map<string, WritableSignal<boolean>>();
  private unsubEvalMap = new Map<string, () => void>();


  private _loading = signal(true);
  readonly loading = computed(() => this._loading());
  readonly chats = signal<ChatSummary[]>([]);

  constructor(private auth: AuthService, /*…resto de inyecciones…*/) {
    // Cada vez que cambie el usuario:
    this.auth.onAuthStateChanged(user => {
      // 1) Desuscribo listener de chats anterior
      if (this.unsubChats) {
        this.unsubChats();
        this.unsubChats = undefined;
      }
      // 2) Limpio estado local
      this.clearAllEvaluations();
      this.chats.set([]);
      this._loading.set(true);

      // 3) Si hay usuario, vuelvo a subscribir
      if (user?.uid) {
        this.listenChatsForUser(user.uid);
      }
    });
  }

  private clearAllEvaluations() {
  this.unsubEvalMap.forEach(unsub => unsub());
  this.unsubEvalMap.clear();
  this.evaluacionActivaMap.clear();
}

  escucharEvaluacion(chatId: string): void {
  const signalEval = signal(false);
  this.evaluacionActivaMap.set(chatId, signalEval);

  // Si hubiera un listener previo, lo cancelamos
  this.unsubEvalMap.get(chatId)?.();

  const unsub = this.firestoreService.listenDoc(
    'chats',
    chatId,
    doc => signalEval.set(!!doc?.['evaluacionActiva'])
  );
  this.unsubEvalMap.set(chatId, unsub);
}

unsubscribirEvaluacion(chatId: string): void {
  this.unsubEvalMap.get(chatId)?.();
  this.unsubEvalMap.delete(chatId);
}

  ngOnDestroy() {
    if (this.unsubChats) this.unsubChats();
    if (this.unsubAuth) this.unsubAuth();
    this.unsubEvalMap.forEach(unsub => unsub());
  }

  /** Crea el ID consistente de chat */
  getChatId(a: string, b: string): string {
    return [a, b].sort().join('_');
  }

  /** Trae datos de perfil de usuario */
  async getUsuario(uid: string): Promise<ChatUsuario | null> {
    try {
      const data = await this.firestoreService.getDocById<any>('usuarios', uid);
      if (!data) return null;
      const fotosValidas = (data.fotos ?? []).filter((f: any) => !f.eliminada);
      return {
        uid,
        name: data.nombre ?? 'Desconocido',
        profilePhoto: fotosValidas[0]?.url ?? '',
        photos: fotosValidas.map((f: any) => f.url)
      };
    } catch (e) {
      console.error(`[ChatService] Error al obtener usuario ${uid}`, e);
      return null;
    }
  }

  getUsuario$(uid: string): Observable<ChatUsuario | null> {
    return this.firestoreService.listenDoc$('usuarios', uid).pipe(map(data => {
      if (!data) return null;
      const fotosValidas = (data["fotos"] ?? []).filter((f: any) => !f.eliminada);
      return {
        uid,
        name: data["nombre"] ?? 'Desconocido',
        profilePhoto: fotosValidas[0]?.url ?? '',
        photos: fotosValidas.map((f: any) => f.url)
      };
    }));
  }

  /** Envío de mensaje (texto o media) */
  async enviarMensaje(
    chatId: string,
    texto: string,
    toId: string,
    uid: string,
    fromName: string,
    fotoUrlFrom: string,
    extra: {
      mediaUrl?: string;
      mediaType?: 'image' | 'video' | 'audio' | 'gift';
      [key: string]: any;
    } = {}
  ): Promise<void> {
    const textoLimpio = texto.trim();
    const tieneMedia = !!extra.mediaUrl;
    if (!textoLimpio && !tieneMedia) {
      throw new Error('El mensaje no puede estar vacío.');
    }
    if (textoLimpio.length > 1000) {
      throw new Error('El mensaje es demasiado largo.');
    }

    const fecha = Timestamp.now();
    const mensaje: any = {
      fromId: uid,
      toId,
      fromName,
      fotoUrlFrom,
      fecha,
      leido: false,
      ...extra
    };
    if (textoLimpio) mensaje.texto = textoLimpio;

    // 1) Agrega a subcolección de mensajes
    await this.firestoreService.addDoc(`chats/${chatId}/mensajes`, mensaje);

    // 2) Actualiza doc principal de chat (contador, ultimoMensaje, etc.)
    const chatDoc = await this.firestoreService.getDocById<any>('chats', chatId);
    const [a, b] = chatId.split('_').sort();
    const contador = chatDoc?.contadorMensajes || {
      total: 0, enviadosPorA: 0, enviadosPorB: 0, umbralActivacion: null as number | null
    };
    contador.total++;
    if (uid === a) contador.enviadosPorA++;
    if (uid === b) contador.enviadosPorB++;
    if (
      contador.enviadosPorA >= 15 &&
      contador.enviadosPorB >= 15 &&
      contador.umbralActivacion === null
    ) {
      contador.umbralActivacion = contador.total + Math.floor(Math.random() * 21) + 10;
    }
    const evaluarActivo =
      contador.umbralActivacion !== null &&
      contador.total >= contador.umbralActivacion;

    const nuevoChatData = {
      usuarios: chatDoc?.usuarios ?? [a, b],
      ultimoMensaje: mensaje,
      contadorMensajes: contador,
      evaluacionActiva: evaluarActivo
    };
    await this.firestoreService.setDoc('chats', chatId, nuevoChatData);

    // 3) Índice en subcolección de cada usuario
    await Promise.all([
      this.firestoreService.setDoc(`usuarios/${a}/chats`, chatId, { creadoEn: fecha }),
      this.firestoreService.setDoc(`usuarios/${b}/chats`, chatId, { creadoEn: fecha })
    ]);

    // 4) Estadísticas
    await this.estadisticasService.incrementarCampo('totalMensajes', 1);
    await this.estadisticasService.incrementarCampo('mensajesHoy', 1);
  }

  async enviarMedia(
    chatId: string,
    file: File,
    toId: string,
    fromName: string,
    fotoUrlFrom:string,
  ): Promise<void> {
    const uid = this.authService.uid!;
    const { url: mediaUrl, tipo: mediaType } =
      await this.chatMediaService.subirMediaChat(chatId, file);
    await this.enviarMensaje(chatId, '', toId, uid, fromName, fotoUrlFrom, { mediaUrl, mediaType });
  }

  /** Marca mensajes como leídos para el usuario actual */
  async marcarMensajesComoLeidos(chatId: string, currentUid: string): Promise<void> {
    try {
      const mensajes = await this.firestoreService.getFilteredCollection<any>(
        `chats/${chatId}/mensajes`,
        [
          where('toId', '==', currentUid),
          where('leido', '==', false)
        ]
      );
      await Promise.all(
        mensajes.map(m => this.firestoreService.updateDoc(
          `chats/${chatId}/mensajes`, m.uid, { leido: true }
        ))
      );
      // actualiza flag en doc principal
      const chat = await this.firestoreService.getDocById<any>('chats', chatId);
      const ultimo = chat?.ultimoMensaje;
      if (ultimo && ultimo.fromId !== currentUid && !ultimo.leido) {
        await this.firestoreService.updateDoc('chats', chatId, {
          'ultimoMensaje.leido': true
        });
      }
      // actualiza señal local
      const otherUid = ultimo
        ? (ultimo.fromId === currentUid ? ultimo.toId! : ultimo.fromId)
        : chatId.split('_').find(x => x !== currentUid)!;
      const lista = this.chats();
      const idx = lista.findIndex(c => c.uid === otherUid);
      if (idx !== -1) {
        const copia = [...lista];
        copia[idx] = { ...copia[idx], mensajesNoLeidos: 0 };
        this.chats.set(copia);
      }
    } catch (e) {
      console.error('[ChatService] Error al marcar mensajes como leídos', e);
    }
  }

  /** Obtiene listado de chats con conteo de mensajes no leídos */
  async obtenerChatsDelUsuario(): Promise<ChatSummary[]> {
    const me = this.authService.uid!;
    const bloqueados = await this.bloqueosService.obtenerBloqueados();
    const bloqueadoPor = await this.bloqueosService.obtenerBloqueadoPor();
    const snap = await this.firestoreService.getCollection<any>(`usuarios/${me}/chats`);
    const resultados: ChatSummary[] = [];

    for (const docSnap of snap) {
      try {
        const chatId = docSnap.uid;
        const chatDoc = await this.firestoreService.getDocById<any>('chats', chatId);
        if (!chatDoc?.ultimoMensaje) continue;

        const { fromId, toId, texto, fecha, leido } = chatDoc.ultimoMensaje;
        const otrasUID = fromId === me ? toId : fromId;
        if (
          bloqueados.some(b => b.uid === otrasUID) ||
          bloqueadoPor.some(b => b.uid === otrasUID)
        ) continue;

        const perfil = await this.getUsuario(otrasUID);
        if (!perfil) continue;
        const promedios = await this.evaluarService.obtenerPromedios(otrasUID);
        const valores = Object.values(promedios);
        const promedioGeneral = valores.length
          ? Math.round((valores.reduce((a, b) => a + b, 0) / valores.length) * 10) / 10
          : undefined;

        // conteo de no leídos
        const noLeidos = await this.firestoreService.getFilteredCollection<any>(
          `chats/${chatId}/mensajes`,
          [
            where('toId', '==', me),
            where('leido', '==', false)
          ]
        );

        resultados.push({
          uid: otrasUID,
          nombre: perfil.name,
          foto: perfil.profilePhoto,
          promedioGeneral,
          mensajesNoLeidos: noLeidos.length,
          ultimoMensaje: { texto, uid: fromId, fecha, leido }
        });
      } catch (e) {
        console.error('[ChatService] Error procesando chat', e);
      }
    }

    return resultados.sort(
      (a, b) => b.ultimoMensaje.fecha.seconds - a.ultimoMensaje.fecha.seconds
    );
  }

  /** Escucha en tiempo real los chats del usuario */
  private listenChatsForUser(uid: string): void {
    if (this.unsubChats) {
      this.unsubChats();
      this.unsubChats = undefined;
    }
    this._loading.set(true);
    this.unsubChats = this.firestoreService.listenCollection(
      `usuarios/${uid}/chats`,
      async snap => {
        const summaries = await Promise.all(snap.map(async docSnap => {
          try {
            const chatId = docSnap.uid;
            const chatDoc = await this.firestoreService.getDocById<any>('chats', chatId);
            if (!chatDoc?.ultimoMensaje) return null;

            const { fromId, toId, texto, fecha, leido } = chatDoc.ultimoMensaje;
            const otrasUID = fromId === uid ? toId : fromId;
            if (this.bloqueosService.uidsBloqueados().has(otrasUID)) return null;

            const perfil = await this.getUsuario(otrasUID);
            if (!perfil) return null;

            const promedios = await this.evaluarService.obtenerPromedios(otrasUID);
            const valores = Object.values(promedios);
            const promedioGeneral = valores.length
              ? Math.round((valores.reduce((a, b) => a + b, 0) / valores.length) * 10) / 10
              : undefined;

            const noLeidos = await this.firestoreService.getFilteredCollection<any>(
              `chats/${chatId}/mensajes`,
              [
                where('toId', '==', uid),
                where('leido', '==', false)
              ]
            );

            return {
              uid: otrasUID,
              nombre: perfil.name,
              foto: perfil.profilePhoto,
              promedioGeneral,
              mensajesNoLeidos: noLeidos.length,
              ultimoMensaje: { texto, uid: fromId, fecha, leido }
            } as ChatSummary;
          } catch {
            return null;
          }
        }));

        this.chats.set(
          summaries.filter((s): s is ChatSummary => !!s)
            .sort((a, b) => b.ultimoMensaje.fecha.seconds - a.ultimoMensaje.fecha.seconds)
        );
        this._loading.set(false);
      }
    );
  }

  escucharMensajes(chatId: string, callback: (ms: any[]) => void) {
    return this.firestoreService.listenCollectionQuery<any>(
      `chats/${chatId}/mensajes`,
      [orderBy('fecha', 'asc')],
      callback
    );
  }
}
